"use client";

import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui";
import { Check, Clock, type LucideIcon, Smartphone, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { KeyboardEvent, MouseEvent } from "react";

import {
  AdvertisedPay,
  PayPlaceholder,
  useJoinerRate,
} from "@/components/opportunities/advertised-pay";
import {
  ConnectionFacepile,
  type FacepileProfile,
} from "@/components/referrals/connection-facepile";
import { useOpportunityShareLink } from "@/hooks/use-opportunity-share-link";
import { usePlatform } from "@/hooks/use-platform";
import { useReferralCap } from "@/hooks/use-referral-cap";
import { getCloudFrontUrl } from "@/lib/cloudfront";
import {
  computeDeviceRestriction,
  type DeviceType,
  formatReferralCapFor,
  formatReferralEstimate,
  getSubmissionStatusDisplay,
  type ParticipantSubmissionStatus,
} from "@/lib/opportunity-utils";
import { cn } from "@/lib/utils";

export interface OpportunityListingCard {
  id: string;
  token: string;
  studyId: string | null;
  title: string | null;
  shortDescription: string | null;
  tags: string[] | null;
  /** Hand-written ranges on the static audience pages; no live card reads them. */
  payRateMin?: string | null;
  payRateMax?: string | null;
  payRateFrequency: string;
  estimatedDurationMins?: number | null;
  category?: string;
  workMode: string;
  opportunityType: string;
  referralBonusAmount: string | null;
  referralCommissionBps: number;
  publishedAt: Date | string | null;
  requirements?: string[] | null;
  referralTitle?: string | null;
  referralShortDescription?: string | null;
  responseProgress?: {
    usedSlots: number;
    targetSlots: number;
  } | null;
  spotsRemaining?: number | null;
  statusLabel?: string | null;
  statusIcon?: LucideIcon | null;
  statusClassName?: string | null;
  allowedDeviceTypes?: DeviceType[] | null;
}

interface OpportunityCardProps {
  opportunity: OpportunityListingCard;
  size?: "default" | "compact";
  variant?: "default" | "referral";
  onSelect?: (opportunity: OpportunityListingCard) => void;
  submissionStatus?: ParticipantSubmissionStatus | null;
  openInNewTab?: boolean;
  className?: string;
  referredBy?: { name: string; image: string | null } | null;
  // Only meaningful on referral-variant cards: indicates the participant
  // already submitted to this study but is still eligible to refer.
  alreadyParticipated?: boolean;
  // When set, the card recedes (muted fill) to signal "refer only, not for
  // you". Only the browse grid passes this; other surfaces stay neutral.
  eligibilityState?: "qualifies" | "not_a_fit";
  // Drop the card's top row (brand/spots/action hint). The browse grid uses
  // this so the surrounding section heading ("Refer & earn") carries the
  // framing instead of a per-card bar.
  hideHeader?: boolean;
  // Connections who fit this study, batched at the list parent (one query).
  facepile?: {
    totalCount: number;
    topProfiles: FacepileProfile[];
  } | null;
}

function getStatusIcon(status: ParticipantSubmissionStatus) {
  switch (status) {
    case "APPROVED":
    case "PAID":
      return <Check size={12} />;
    case "IN_PROGRESS":
    case "PROCESSING":
    case "AWAITING_REVIEW":
      return <Clock size={12} />;
    case "SCREENED_OUT":
    case "NOT_SELECTED":
    case "EXPIRED":
      return <X size={12} />;
  }
}

export function OpportunityCard({
  opportunity,
  size = "default",
  variant = "default",
  onSelect,
  submissionStatus,
  openInNewTab,
  className,
  referredBy,
  alreadyParticipated,
  eligibilityState,
  hideHeader,
  facepile,
}: OpportunityCardProps) {
  const { platform } = usePlatform();
  const referralCapAmount = useReferralCap();
  const isReferral = variant === "referral";
  const facepileProfiles = facepile?.topProfiles ?? [];
  const hasFacepile = facepileProfiles.length > 0;
  const facepileCount = facepile?.totalCount ?? 0;
  const facepileFitsLabel = `${facepileCount} ${facepileCount === 1 ? "person" : "people"} you know ${facepileCount === 1 ? "matches" : "match"} this study`;
  // One fit -> first name, several -> "N you know" (the blue highlight).
  const facepileFirstName = facepileProfiles[0]?.firstName?.trim() || null;
  const referHighlight =
    facepileCount === 1 && facepileFirstName
      ? facepileFirstName
      : `${facepileCount} you know`;
  const referVerb = facepileCount === 1 ? "matches" : "match";
  // Device restriction chip: only meaningful when the participant would be
  // applying (not referring someone else). On referral cards we suppress it
  // because the referrer's device doesn't matter.
  const deviceRestriction = isReferral
    ? null
    : computeDeviceRestriction(opportunity.allowedDeviceTypes, platform);
  const isCompact = size === "compact";
  const slotsRemainingLabel = (() => {
    if (isReferral || referredBy || !opportunity.responseProgress) {
      return null;
    }

    const remainingSlots = Math.max(
      0,
      opportunity.responseProgress.targetSlots -
        opportunity.responseProgress.usedSlots,
    );
    return `${remainingSlots > 99 ? "99+" : remainingSlots} spots left`;
  })();

  // What the person referred would be paid, never the viewer's own rate, and
  // nothing at all until it has loaded: a listing column standing in for it
  // is a figure the card would take back.
  const joiner = useJoinerRate(opportunity.studyId);
  const joinerRate = joiner.pending ? undefined : joiner.rate;
  const referralEstimate = isReferral
    ? formatReferralEstimate(joinerRate)
    : null;
  // Only on referral cards: who the referrer should be looking for. Eligible
  // viewers have the field stripped server-side, so it never shows on a card
  // they can apply to.
  const audienceLabel = isReferral
    ? opportunity.referralTitle?.trim() || null
    : null;
  // On default cards (not the referral variant), surface the per-referral
  // cap as a CTA-style badge in the bottom-right when the slot isn't
  // already occupied by a referrer attribution.
  const defaultReferralCap =
    !isReferral && !isCompact
      ? formatReferralCapFor(joinerRate, referralCapAmount)
      : null;

  // Lazy share-link: don't mint on render or on hover (would fan out N rows
  // and N `referral_link_minted` events per browsing session). `onPointerDown`
  // primes the cache right before the click event so the click reads the
  // canonical `/refer/{token}` URL from cache. The hook handles cleanup,
  // in-flight guard, and PostHog event shape so this surface stays in sync
  // with the detail-panel and marketing surfaces.
  const referralShareEnabled =
    !isReferral && !isCompact && Number(joinerRate?.rateUsd) > 0;
  const referralShareLabel = defaultReferralCap
    ? `Copy referral link, earn up to ${defaultReferralCap} per referral`
    : "Copy referral link";
  const { copyLink, prefetchLink, copied } = useOpportunityShareLink({
    token: opportunity.token,
    source: "DASHBOARD",
    enabled: referralShareEnabled,
    lazy: true,
  });

  const handleReferActivate = (
    event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    void copyLink();
  };

  const handleReferKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      handleReferActivate(event);
    }
  };

  const referrerAvatarUrl = getCloudFrontUrl(referredBy?.image ?? null);

  // Colored eligibility pill that replaces the Terac brand row in the mixed
  // browse grid. It is the primary signal separating Apply cards from Refer
  // cards when they sit side by side.
  const statusPill =
    eligibilityState === "qualifies" ? (
      <span className="border-border bg-background text-foreground inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
        <Check size={10} strokeWidth={2.5} />
        You qualify
      </span>
    ) : eligibilityState === "not_a_fit" ? (
      <span className="border-border bg-card text-muted-foreground inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
        Not a fit
      </span>
    ) : null;

  const spotsLeft = opportunity.spotsRemaining ?? null;
  const spotsLeftLabel =
    spotsLeft != null
      ? `${spotsLeft > 99 ? "99+" : spotsLeft} spots left`
      : null;
  const headerSpotsLabel = spotsLeftLabel ?? slotsRemainingLabel;

  // For referral cards, show up to 2 key requirements as context
  const visibleRequirements =
    isReferral && opportunity.requirements
      ? opportunity.requirements.slice(0, 2)
      : [];

  const content = isReferral ? (
    <>
      {/* Header: eligibility pill (or brand) + spots + refer CTA */}
      {!hideHeader && (
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {statusPill ?? (
              <>
                <Image
                  src="/brand/logo.svg"
                  alt="Terac"
                  width={15}
                  height={15}
                />
                <span className="text-muted-foreground/60 text-[12px] font-normal">
                  Terac
                </span>
              </>
            )}
            {alreadyParticipated && (
              <span className="border-border bg-muted-foreground/5 text-muted-foreground ml-0.5 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium">
                <Check size={10} />
                Already participated
              </span>
            )}
            {headerSpotsLabel && !alreadyParticipated && (
              <span className="text-muted-foreground/60 ml-0.5 text-[12px] font-normal whitespace-nowrap">
                {headerSpotsLabel}
              </span>
            )}
          </div>
          <span className="group-hover:text-brand-blue/70 flex items-center gap-1 text-[12.5px] font-normal whitespace-nowrap text-transparent transition-colors duration-200">
            Refer someone
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="-rotate-45 transition-transform duration-200"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </span>
        </div>
      )}

      {/* Always the public title, so a referrer sees the study under the same
          name as the person they send to it. The referral title is an audience
          label and sits above as the "who". When the top bar is hidden, show
          known availability in place of the Refer hint until hover. */}
      {audienceLabel && (
        <div className="text-muted-foreground/80 mb-1 line-clamp-1 text-[11.5px] leading-[1.4] font-medium">
          For {audienceLabel}
        </div>
      )}
      {hideHeader ? (
        <div className="mb-1.5 flex items-start justify-between gap-2.5">
          <div className="text-foreground line-clamp-1 min-w-0 flex-1 text-[16px] leading-[1.35] font-semibold tracking-[-0.015em]">
            {opportunity.title}
          </div>
          <span className="mt-0.5 grid shrink-0 items-center justify-items-end">
            {spotsLeftLabel && (
              <span className="text-muted-foreground/50 col-start-1 row-start-1 text-[12px] font-light whitespace-nowrap transition-opacity duration-200 group-hover:opacity-0">
                {spotsLeftLabel}
              </span>
            )}
            <span
              className={`text-muted-foreground/50 group-hover:text-brand-blue col-start-1 row-start-1 flex items-center gap-1 text-[12px] font-light transition-opacity duration-200 ${spotsLeftLabel ? "opacity-0 group-hover:opacity-100" : ""}`}
            >
              Refer
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="-rotate-45 transition-transform duration-200"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </span>
        </div>
      ) : (
        <div className="text-foreground mb-1.5 line-clamp-2 text-[16px] leading-[1.35] font-semibold tracking-[-0.015em]">
          {opportunity.title}
        </div>
      )}

      {/* Context for who to refer: prefer referralShortDescription, then requirements, then shortDescription */}
      {opportunity.referralShortDescription ? (
        <div
          className={`text-muted-foreground mb-2.5 text-[13.5px] leading-[1.55] font-light ${hideHeader ? "line-clamp-2 min-h-[2.6rem]" : "line-clamp-2"}`}
        >
          {opportunity.referralShortDescription}
        </div>
      ) : visibleRequirements.length > 0 ? (
        <div
          className={`mb-2.5 space-y-1 ${hideHeader ? "min-h-[2.6rem]" : ""}`}
        >
          {visibleRequirements.map((req) => (
            <div
              key={req}
              className="text-muted-foreground flex items-start gap-1.5 text-[13px] leading-[1.5] font-light"
            >
              <span className="bg-muted-foreground/30 mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full" />
              <span className="line-clamp-1">{req}</span>
            </div>
          ))}
        </div>
      ) : (
        <div
          className={`text-muted-foreground mb-2.5 text-[13.5px] leading-[1.55] font-light ${hideHeader ? "line-clamp-2 min-h-[2.6rem]" : "line-clamp-2"}`}
        >
          {opportunity.shortDescription}
        </div>
      )}

      {/* Bottom: earnings left, "who you know qualifies" facepile bottom-right */}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        {/* The commission or nothing. What this card's "Earn" promises is a
            share of the study's pay, so the study's own figure must never
            stand in for it -- at 20% that would overstate it fivefold. */}
        {isReferral && joiner.pending && (
          <span className="shrink-0 whitespace-nowrap">
            <span className="text-muted-foreground/50 text-[12px] font-light">
              Earn{" "}
            </span>
            <PayPlaceholder className="h-[15px] w-12" />
          </span>
        )}
        {referralEstimate && (
          <span className="shrink-0 whitespace-nowrap">
            <span className="text-muted-foreground/50 text-[12px] font-light">
              Earn{" "}
            </span>
            <strong className="text-foreground text-[15px] font-bold tracking-[-0.02em]">
              {referralEstimate.amount}
            </strong>
            {referralEstimate.per && (
              <span className="text-muted-foreground/50 text-[12px] font-light">
                {" "}
                {referralEstimate.per}
              </span>
            )}
          </span>
        )}
        {hasFacepile ? (
          <span className="inline-flex shrink-0 items-center gap-1.5">
            <ConnectionFacepile
              profiles={facepileProfiles}
              totalCount={facepileCount}
            />
            <span className="text-[12px]">
              <span className="text-brand-blue font-medium">
                {referHighlight}
              </span>{" "}
              <span className="text-foreground font-light">{referVerb}</span>
            </span>
          </span>
        ) : opportunity.statusLabel ? (
          <span
            className={`inline-flex shrink-0 items-center gap-1 text-[11px] font-medium whitespace-nowrap ${opportunity.statusClassName ?? "text-muted-foreground/70"}`}
          >
            {opportunity.statusIcon ? (
              <opportunity.statusIcon className="h-3 w-3" strokeWidth={2.5} />
            ) : null}
            {opportunity.statusLabel}
          </span>
        ) : null}
      </div>
    </>
  ) : (
    <>
      {/* Header: brand + apply link */}
      {!hideHeader && (
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {statusPill ?? (
              <>
                <Image
                  src="/brand/logo.svg"
                  alt="Terac"
                  width={isCompact ? 14 : 15}
                  height={isCompact ? 14 : 15}
                />
                <span
                  className={`text-muted-foreground/60 font-normal ${isCompact ? "text-[11px]" : "text-[12px]"}`}
                >
                  Terac
                </span>
              </>
            )}
            {deviceRestriction && (
              <span className="border-border bg-muted-foreground/5 text-muted-foreground ml-0.5 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium">
                <Smartphone size={10} />
                {deviceRestriction.label}
              </span>
            )}
            {!isCompact && headerSpotsLabel && (
              <span className="text-muted-foreground/60 ml-0.5 text-[12px] font-normal whitespace-nowrap">
                {headerSpotsLabel}
              </span>
            )}
          </div>
          {submissionStatus ? (
            <span
              className={`flex items-center gap-1 text-[12.5px] font-normal ${getSubmissionStatusDisplay(submissionStatus).className}`}
            >
              {getStatusIcon(submissionStatus)}
              {getSubmissionStatusDisplay(submissionStatus).label}
            </span>
          ) : (
            <span className="group-hover:text-brand-blue/70 flex items-center gap-1 text-[12.5px] font-normal text-transparent transition-colors duration-200">
              Apply
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="-rotate-45 transition-transform duration-200"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          )}
        </div>
      )}

      {/* Title (+ inline Apply hint when the top bar is hidden) */}
      {hideHeader ? (
        <div className="mb-1.5 flex items-start justify-between gap-2.5">
          <div className="text-foreground line-clamp-1 min-w-0 flex-1 text-[16px] leading-[1.35] font-semibold tracking-[-0.015em]">
            {opportunity.title}
          </div>
          {submissionStatus ? (
            <span
              className={`mt-0.5 flex shrink-0 items-center gap-1 text-[12.5px] font-normal ${getSubmissionStatusDisplay(submissionStatus).className}`}
            >
              {getStatusIcon(submissionStatus)}
              {getSubmissionStatusDisplay(submissionStatus).label}
            </span>
          ) : (
            // Spots-left at rest; on hover it crossfades to a blue "Apply"
            // hint, freeing the bottom-right for the Refer pill. Both share one
            // grid cell so the fade swaps in place without shifting layout.
            <span className="mt-0.5 grid shrink-0 items-center justify-items-end">
              {!isCompact && spotsLeftLabel && (
                <span className="text-muted-foreground/50 col-start-1 row-start-1 text-[12px] font-light whitespace-nowrap transition-opacity duration-200 group-hover:opacity-0">
                  {spotsLeftLabel}
                </span>
              )}
              <span className="text-brand-blue col-start-1 row-start-1 flex items-center gap-1 text-[12px] font-light whitespace-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                Apply
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="-rotate-45 transition-transform duration-200"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </span>
          )}
        </div>
      ) : (
        <div
          className={`text-foreground leading-[1.35] font-semibold tracking-[-0.015em] ${isCompact ? "mb-1 text-[14px]" : "mb-1.5 text-[16px]"}`}
        >
          {opportunity.title}
        </div>
      )}

      {/* Description */}
      <div
        className={`text-muted-foreground leading-[1.55] font-light ${isCompact ? "mb-2 line-clamp-2 text-[11px]" : "mb-2.5 text-[13.5px]"} ${hideHeader ? "line-clamp-2 min-h-[2.6rem]" : !isCompact ? "line-clamp-2" : ""}`}
      >
        {opportunity.shortDescription}
      </div>

      {/* Bottom: pay + social proof */}
      <div className="mt-auto flex items-center gap-2.5">
        {/* One line, so a study running a temporary rate does not make its card
            taller than the ones beside it. The struck figure steps down only a
            little here: a fixed percentage that reads right against the hero's
            42px is a caption against a card's 15. */}
        <AdvertisedPay
          studyId={opportunity.studyId}
          listing={opportunity}
          className="min-w-0 shrink whitespace-nowrap"
          amountClassName={`text-foreground font-bold tracking-[-0.02em] ${isCompact ? "text-[13px]" : "text-[15px]"}`}
          suffixClassName={`text-muted-foreground/50 font-light ${isCompact ? "text-[10px]" : "text-[12px]"}`}
          standingClassName={`text-muted-foreground/45 ${isCompact ? "text-[12px]" : "text-[14px]"}`}
          temporaryClassName={isCompact ? "text-[10.5px]" : "text-[12px]"}
        />
        <div className="ml-auto flex items-center gap-1.5">
          {referredBy ? (
            <div className="flex items-center gap-1.5">
              {referrerAvatarUrl ? (
                <Image
                  src={referrerAvatarUrl}
                  alt=""
                  width={isCompact ? 18 : 20}
                  height={isCompact ? 18 : 20}
                  className="shrink-0 rounded-full object-cover"
                />
              ) : (
                <div
                  className="bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                  style={{
                    width: isCompact ? 18 : 20,
                    height: isCompact ? 18 : 20,
                  }}
                >
                  {referredBy.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className={`text-muted-foreground/60 font-light whitespace-nowrap ${isCompact ? "text-[10px]" : "text-[11px]"}`}
              >
                Referred by{" "}
                <span className="text-muted-foreground font-medium">
                  {referredBy.name}
                </span>
              </span>
            </div>
          ) : referralShareEnabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={
                    hasFacepile
                      ? `${facepileFitsLabel}. ${referralShareLabel}`
                      : referralShareLabel
                  }
                  onClick={handleReferActivate}
                  onKeyDown={handleReferKeyDown}
                  onPointerDown={prefetchLink}
                  className="focus-visible:outline-brand-blue inline-flex cursor-pointer items-center gap-1.5 rounded-full whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {/* Label and "Copied" share a grid cell so they crossfade in place. */}
                  <span className="grid items-center justify-items-end">
                    <span
                      className={`col-start-1 row-start-1 flex items-center gap-1.5 transition-opacity duration-200 ${copied ? "opacity-0" : "opacity-100"}`}
                    >
                      {hasFacepile && (
                        <ConnectionFacepile
                          profiles={facepileProfiles}
                          totalCount={facepileCount}
                        />
                      )}
                      <span className="text-[12px]">
                        {hasFacepile ? (
                          <>
                            <span className="text-brand-blue font-medium">
                              {referHighlight}
                            </span>
                            <span className="text-foreground font-light">
                              {" "}
                              {referVerb}
                              {defaultReferralCap
                                ? ` · ${defaultReferralCap}`
                                : ""}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-brand-blue font-medium">
                              Refer
                            </span>
                            {defaultReferralCap && (
                              <span className="text-foreground font-light">
                                {" "}
                                · {defaultReferralCap}
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={`col-start-1 row-start-1 flex items-center justify-end gap-1 transition-opacity duration-200 ${copied ? "opacity-100" : "opacity-0"}`}
                    >
                      <Check
                        size={11}
                        strokeWidth={2.5}
                        className="text-foreground"
                      />
                      <span className="text-foreground text-[12px] font-medium">
                        Copied
                      </span>
                    </span>
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-pretty">
                <p className="text-center text-[12.5px] leading-[1.5]">
                  {hasFacepile ? `${facepileFitsLabel}. ` : ""}
                  {defaultReferralCap
                    ? `Earn up to ${defaultReferralCap} for every referral. Click to copy referral link`
                    : "Click to copy referral link"}
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            isCompact &&
            headerSpotsLabel && (
              <span className="text-muted-foreground/70 text-[10px] font-medium">
                {headerSpotsLabel}
              </span>
            )
          )}
        </div>
      </div>
    </>
  );

  // Ineligible cards recede: a muted fill on the whole card signals "you
  // can't take this, only refer it" at a glance, distinct from the clean
  // card surface of opportunities you qualify for. Only applied in the
  // browse grid (where eligibilityState is set); other surfaces stay neutral.
  const surfaceClass =
    eligibilityState === "not_a_fit" ? "bg-muted/50" : "bg-card";

  const baseClasses = isCompact
    ? `group flex h-full select-none flex-col cursor-pointer rounded-xl border border-border ${surfaceClass} px-3.5 py-3 transition-colors duration-200 ease-out hover:border-foreground/15 animate-[fadeUp_0.4s_ease_both]`
    : `group flex h-full select-none flex-col cursor-pointer rounded-xl border border-border ${surfaceClass} px-4 py-4 transition-colors duration-200 ease-out hover:border-foreground/15 animate-[fadeUp_0.4s_ease_both]`;

  const cardClasses = cn(baseClasses, className);

  if (onSelect) {
    return (
      <div
        className={cardClasses}
        onClick={() => {
          onSelect(opportunity);
        }}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      href={`/opportunities/${opportunity.token}`}
      className={`${cardClasses} no-underline`}
      style={{ color: "inherit" }}
      {...(openInNewTab
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {content}
    </Link>
  );
}
